import { useRef, useCallback } from 'react';

/**
 * Custom hook for scrolling to a section
 * Returns a ref to attach to the target section and a scroll function
 * 
 * @param {Object} options
 * @param {string} options.behavior - Scroll behavior: 'smooth' | 'instant' (default: 'smooth')
 * @param {string} options.block - Block alignment: 'start' | 'center' | 'end' | 'nearest' (default: 'start')
 * @param {number} options.delay - Delay in ms before scrolling (default: 100)
 * @param {number} options.offset - Offset in pixels from top (default: 80 for AppBar height)
 * @returns {Object} { sectionRef, scrollToSection }
 * 
 * @example
 * const { sectionRef, scrollToSection } = useScrollToSection();
 * 
 * // In HeaderSection
 * <Button onClick={() => scrollToSection()}>Go to Processor</Button>
 * 
 * // In Main.js
 * <HeaderSection scrollToProcessor={scrollToSection} />
 * <ProcessorSection ref={sectionRef} />
 */
export const useScrollToSection = ({
    behavior = 'smooth',
    block = 'start',
    delay = 100,
    offset = 80, // Default AppBar height
} = {}) => {
    const sectionRef = useRef(null);

    const scrollToSection = useCallback(() => {
        const scrollToElement = () => {
            if (sectionRef?.current) {
                const element = sectionRef.current;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - offset;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: behavior,
                });
            }
        };

        if (delay > 0) {
            setTimeout(scrollToElement, delay);
        } else {
            scrollToElement();
        }
    }, [behavior, block, delay, offset]);

    return { sectionRef, scrollToSection };
};

